import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import "./MediaRepository.scss";
import {
  getMediaRepositoryApi,
  quarantineMediaApi,
  restoreMediaApi,
  deleteMediaApi,
} from "../../../../api/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapApiType(apiType = "", mimeType = "") {
  const t = apiType.toUpperCase();
  const m = mimeType.toLowerCase();
  if (t === "IMAGE") return "Image";
  if (t === "VIDEO") return "Video";
  if (t === "AUDIO") return "Audio";
  if (m.includes("pdf")) return "PDF";
  if (m.includes("spreadsheet") || m.includes("excel") || m.includes("csv"))
    return "Excel";
  if (m.includes("presentation") || m.includes("powerpoint"))
    return "Presentation";
  if (m.includes("zip") || m.includes("archive") || m.includes("compressed"))
    return "Archive";
  if (m.includes("word") || m.includes("document")) return "Document";
  if (t === "APPLICATION") return "Document";
  return "Document";
}

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + " MB";
  if (bytes >= 1024) return Math.round(bytes / 1024) + " KB";
  return bytes + " B";
}

function mxcToProxyUrl(mxcUrl) {
  if (!mxcUrl) return null;
  if (mxcUrl.startsWith("http://") || mxcUrl.startsWith("https://")) {
    return mxcUrl;
  }
  if (mxcUrl.startsWith("mxc://")) {
    const withoutProtocol = mxcUrl.replace("mxc://", "");
    const [server, mediaId] = withoutProtocol.split("/");
    if (!server || !mediaId) return null;
    const base = "http://localhost:8009";
    return `${base}/murasalone/api/matrix-media/${server}/${mediaId}`;
  }
  return null;
}

function normaliseMedia(m) {
  const rawPreview = m.previewUrl || m.matrixMxcUrl || null;
  const rawDownload = m.downloadUrl || m.matrixMxcUrl || null;
  const API_BASE = "http://localhost:8009";

  return {
    id: m.id,
    name: m.name || "Unnamed",
    type: mapApiType(m.type || "", m.mimeType || ""),
    mimeType: m.mimeType || "",
    room: m.room || m.matrixRoomId || "Unknown Room",
    roomId: m.roomId || m.matrixRoomId || "",
    uploadedBy: m.uploadedBy || "Unknown",
    uploaderInitials: m.uploaderInitials || "UN",
    uploaderColor: m.uploaderColor || "#0062FE",
    size: m.size || formatSize(m.sizeBytes),
    sizeBytes: m.sizeBytes || 0,
    status: (m.status || "available").toLowerCase(),
    uploadedAt: m.uploadedAt ? new Date(m.uploadedAt).toLocaleString() : "—",
    expiresAt: m.expiresAt || "N/A",
    scanResult: m.scanResult || "clean",
    previewUrl: rawPreview
      ? rawPreview.startsWith("http")
        ? rawPreview
        : `${API_BASE}${rawPreview}`
      : null,
    downloadUrl: rawDownload
      ? rawDownload.startsWith("http")
        ? rawDownload
        : `${API_BASE}${rawDownload}`
      : null,
    matrixMxcUrl: m.matrixMxcUrl || null,
  };
}

// Map UI type label → API `type` query param value
const TYPE_TO_API = {
  Image: "image",
  Video: "video",
  PDF: "application",
  Excel: "application",
  Presentation: "application",
  Document: "application",
  Archive: "application",
};

const ALL_TYPES = [
  "Image",
  "Video",
  "PDF",
  "Excel",
  "Presentation",
  "Document",
  "Archive",
];
const PAGE_SIZE = 8;

// ─── Icons ────────────────────────────────────────────────────────────────────
const Ico = {
  Search: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Export: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Dots: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  ),
  Download: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Eye: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Shield: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Restore: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 .49-3.15" />
    </svg>
  ),
  Trash: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  ),
  Clock: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  SortUp: (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  ChevR: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  Close: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Warn: (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  File: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  Database: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  Grid: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  List: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  Refresh: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  ExternalLink: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
};

// ─── File type metadata ───────────────────────────────────────────────────────
function getFileTypeMeta(type) {
  const map = {
    PDF: { color: "#EF4444", bg: "rgba(239,68,68,0.10)", ext: "PDF" },
    Excel: { color: "#22C55E", bg: "rgba(34,197,94,0.10)", ext: "XLS" },
    Image: { color: "#8B5CF6", bg: "rgba(139,92,246,0.10)", ext: "IMG" },
    Video: { color: "#F97316", bg: "rgba(249,115,22,0.10)", ext: "VID" },
    Audio: { color: "#06B6D4", bg: "rgba(6,182,212,0.10)", ext: "AUD" },
    Presentation: { color: "#F59E0B", bg: "rgba(245,158,11,0.10)", ext: "PPT" },
    Document: { color: "#0062FE", bg: "rgba(0,98,254,0.10)", ext: "DOC" },
    Archive: { color: "#6B7280", bg: "rgba(107,114,128,0.10)", ext: "ZIP" },
  };
  return map[type] || { color: "#6B7280", bg: "#F3F4F6", ext: "FILE" };
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        padding: "12px 20px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 500,
        background: type === "error" ? "#FEE2E2" : "#D1FAE5",
        color: type === "error" ? "#991B1B" : "#065F46",
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
      }}
    >
      {type === "error" ? "⚠️" : "✅"} {msg}
    </div>
  );
}

// ─── Row Dropdown ─────────────────────────────────────────────────────────────
function RowMenu({
  file,
  onView,
  onDownload,
  onQuarantine,
  onRestore,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="mr-row-actions" ref={ref}>
      <button className="mr-menu-btn" onClick={() => setOpen((v) => !v)}>
        {Ico.Dots}
      </button>
      {open && (
        <div className="mr-dropdown">
          <button
            className="mr-dropdown__item"
            onClick={() => {
              onView(file);
              setOpen(false);
            }}
          >
            {Ico.Eye} Preview File
          </button>
          <button
            className="mr-dropdown__item"
            onClick={() => {
              onDownload(file);
              setOpen(false);
            }}
          >
            {Ico.Download} Download
          </button>
          {file.status === "quarantined" ? (
            <button
              className="mr-dropdown__item"
              onClick={() => {
                onRestore(file);
                setOpen(false);
              }}
            >
              {Ico.Restore} Restore from Quarantine
            </button>
          ) : (
            <button
              className="mr-dropdown__item mr-dropdown__item--warn"
              onClick={() => {
                onQuarantine(file);
                setOpen(false);
              }}
            >
              {Ico.Shield} Quarantine
            </button>
          )}
          <button
            className="mr-dropdown__item mr-dropdown__item--danger mr-dropdown__item--divider"
            onClick={() => {
              onDelete(file);
              setOpen(false);
            }}
          >
            {Ico.Trash} Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────
function PreviewModal({ file, onClose }) {
  const meta = getFileTypeMeta(file.type);
  const isImage =
    file.type?.toLowerCase() === "image" &&
    file.previewUrl &&
    !file.previewUrl.startsWith("mxc://");

  const isVideo =
    file.type?.toLowerCase() === "video" &&
    file.previewUrl &&
    !file.previewUrl.startsWith("mxc://");

  return (
    <div className="mr-modal-overlay" onClick={onClose}>
      <div className="mr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mr-modal__header">
          <div
            className="mr-modal__file-icon"
            style={{ background: meta.bg, color: meta.color }}
          >
            {Ico.File}
          </div>
          <div>
            <h4 className="mr-modal__title">{file.name}</h4>
            <p className="mr-modal__desc">
              {file.type} · {file.size} · {file.room}
            </p>
          </div>
          <button className="mr-modal__close" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>
        <div className="mr-modal__body">
          {isImage ? (
            <div className="mr-preview-image">
              <img
                src={file.previewUrl}
                alt={file.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: 8,
                  objectFit: "contain",
                  display: "block",
                  margin: "0 auto",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling &&
                    (e.target.nextSibling.style.display = "flex");
                }}
              />
              <div
                className="mr-preview-placeholder"
                style={{ display: "none" }}
              >
                <div
                  className="mr-preview-placeholder__icon"
                  style={{ color: meta.color }}
                >
                  {Ico.File}
                </div>
                <p>Image could not be loaded.</p>
                <p className="mr-preview-placeholder__sub">
                  Download the file to view it.
                </p>
              </div>
            </div>
          ) : isVideo ? (
            <div className="mr-preview-image">
              <video
                src={file.previewUrl}
                controls
                style={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  borderRadius: 8,
                  display: "block",
                  margin: "0 auto",
                  background: "#000",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          ) : (
            <div className="mr-preview-placeholder">
              <div
                className="mr-preview-placeholder__icon"
                style={{ color: meta.color }}
              >
                {Ico.File}
              </div>
              <p>Preview not available in admin portal.</p>
              <p className="mr-preview-placeholder__sub">
                Download the file to view its contents.
              </p>
            </div>
          )}
          <div className="mr-modal__meta-grid">
            {[
              ["Uploaded by", file.uploadedBy],
              ["Upload date", file.uploadedAt],
              ["File size", file.size],
              ["Room", file.room],
              ["Expires", file.expiresAt],
              [
                "Scan result",
                file.scanResult === "flagged" ? "⚠ Flagged" : "✓ Clean",
              ],
              [
                "Status",
                file.status.charAt(0).toUpperCase() + file.status.slice(1),
              ],
            ].map(([k, v]) => (
              <div key={k} className="mr-modal__meta-item">
                <span className="mr-modal__meta-label">{k}</span>
                <span className="mr-modal__meta-val">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mr-modal__footer">
          {file.downloadUrl && !file.downloadUrl.startsWith("mxc://") && (
            <a
              className="mr-btn mr-btn--primary"
              href={file.downloadUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {Ico.Download} Download
            </a>
          )}
          {file.previewUrl &&
            file.type !== "Image" &&
            !file.previewUrl.startsWith("mxc://") && (
              <a
                className="mr-btn mr-btn--secondary"
                href={file.previewUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                {Ico.ExternalLink} Open in Browser
              </a>
            )}
          <button className="mr-btn mr-btn--secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quarantine Confirm Modal ─────────────────────────────────────────────────
function QuarantineModal({ file, onConfirm, onCancel, loading }) {
  return (
    <div className="mr-modal-overlay">
      <div className="mr-modal mr-modal--sm">
        <div className="mr-modal__header">
          <div
            className="mr-modal__file-icon"
            style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B" }}
          >
            {Ico.Warn}
          </div>
          <div>
            <h4 className="mr-modal__title">Quarantine "{file.name}"?</h4>
            <p className="mr-modal__desc">
              The file will be isolated and users will not be able to access it
              until it is reviewed and restored.
            </p>
          </div>
        </div>
        <div className="mr-modal__footer">
          <button className="mr-btn mr-btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="mr-btn mr-btn--warn"
            disabled={loading}
            onClick={onConfirm}
          >
            {Ico.Shield} {loading ? "Quarantining…" : "Quarantine File"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
function DeleteModal({ file, onConfirm, onCancel, loading }) {
  return (
    <div className="mr-modal-overlay">
      <div className="mr-modal mr-modal--sm">
        <div className="mr-modal__header">
          <div
            className="mr-modal__file-icon"
            style={{ background: "rgba(239,68,68,0.10)", color: "#EF4444" }}
          >
            {Ico.Warn}
          </div>
          <div>
            <h4 className="mr-modal__title">Delete "{file.name}"?</h4>
            <p className="mr-modal__desc">
              This action is permanent and cannot be undone. The file will be
              removed from storage.
            </p>
          </div>
        </div>
        <div className="mr-modal__footer">
          <button className="mr-btn mr-btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="mr-btn mr-btn--danger"
            disabled={loading}
            onClick={onConfirm}
          >
            {Ico.Trash} {loading ? "Deleting…" : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Grid Card ────────────────────────────────────────────────────────────────
function FileCard({
  file,
  onView,
  onDownload,
  onQuarantine,
  onRestore,
  onDelete,
}) {
  const meta = getFileTypeMeta(file.type);
  const hasValidPreview =
    (file.type?.toLowerCase() === "image" ||
      file.type?.toLowerCase() === "video") &&
    file.previewUrl &&
    !file.previewUrl.startsWith("mxc://");

  const isVideoCard = file.type?.toLowerCase() === "video";

  return (
    <div
      className={`mr-file-card${file.status === "quarantined" ? " mr-file-card--quarantined" : ""}${file.status === "archived" ? " mr-file-card--archived" : ""}`}
    >
      <div className="mr-file-card__top">
        {hasValidPreview ? (
          <div className="mr-file-card__thumb">
            {isVideoCard ? (
              <video
                src={file.previewUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                  display: "block",
                  background: "#000",
                }}
                muted
                preload="metadata"
                onError={(e) => {
                  if (e.target?.parentNode)
                    e.target.parentNode.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:${meta.bg};color:${meta.color};border-radius:8px;font-size:11px;font-weight:700">${meta.ext}</div>`;
                }}
              />
            ) : (
              <img
                src={file.previewUrl}
                alt={file.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
                onError={(e) => {
                  if (e.target?.parentNode)
                    e.target.parentNode.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:${meta.bg};color:${meta.color};border-radius:8px;font-size:11px;font-weight:700">${meta.ext}</div>`;
                }}
              />
            )}
          </div>
        ) : (
          <div
            className="mr-file-card__icon"
            style={{ background: meta.bg, color: meta.color }}
          >
            <span className="mr-file-card__ext">{meta.ext}</span>
          </div>
        )}
        <RowMenu
          file={file}
          onView={onView}
          onDownload={onDownload}
          onQuarantine={onQuarantine}
          onRestore={onRestore}
          onDelete={onDelete}
        />
      </div>
      <div className="mr-file-card__name" title={file.name}>
        {file.name}
      </div>
      <div className="mr-file-card__meta">
        {file.size} · {file.room}
      </div>
      <div className="mr-file-card__footer">
        <div className="mr-manager-cell">
          <div
            className="mr-manager-cell__avatar"
            style={{ background: file.uploaderColor }}
          >
            {file.uploaderInitials}
          </div>
          <span className="mr-manager-cell__name">{file.uploadedBy}</span>
        </div>
        <span className={`mr-badge mr-badge--${file.status}`}>
          {file.status}
        </span>
      </div>
    </div>
  );
}

// ─── Storage Bar ─────────────────────────────────────────────────────────────
function StorageBar({ files }) {
  const totalBytes = files.reduce((s, f) => s + (f.sizeBytes || 0), 0);
  const maxBytes = 200 * 1024 * 1024;
  const usedPct = Math.min(100, (totalBytes / maxBytes) * 100).toFixed(1);
  const fmt = (b) => (b / 1024 / 1024).toFixed(1) + " MB";

  const byType = ALL_TYPES.map((t) => {
    const bytes = files
      .filter((f) => f.type === t)
      .reduce((s, f) => s + (f.sizeBytes || 0), 0);
    return {
      type: t,
      bytes,
      pct: totalBytes ? ((bytes / totalBytes) * 100).toFixed(1) : "0",
      color: getFileTypeMeta(t).color,
    };
  }).filter((x) => x.bytes > 0);

  return (
    <div className="mr-storage-card">
      <div className="mr-storage-card__header">
        <span>{Ico.Database} Storage Overview</span>
        <span className="mr-storage-card__cap">
          {fmt(totalBytes)} / {fmt(maxBytes)}
        </span>
      </div>
      <div className="mr-storage-bar">
        {byType.length > 0 ? (
          byType.map(({ type, pct, color }) => (
            <div
              key={type}
              className="mr-storage-bar__seg"
              style={{ width: `${pct}%`, background: color }}
              title={`${type}: ${pct}%`}
            />
          ))
        ) : (
          <div
            className="mr-storage-bar__seg"
            style={{ width: "100%", background: "#E5E7EB" }}
          />
        )}
      </div>
      <div className="mr-storage-legend">
        {byType.map(({ type, pct, color }) => (
          <div key={type} className="mr-storage-legend__item">
            <span
              className="mr-storage-legend__dot"
              style={{ background: color }}
            />
            <span>
              {type} {pct}%
            </span>
          </div>
        ))}
      </div>
      <div className="mr-storage-card__pct">
        <span style={{ color: usedPct > 80 ? "#EF4444" : "#0062FE" }}>
          {usedPct}% used
        </span>
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, total, count, onPage }) {
  if (totalPages <= 1 && count === total) return null;
  return (
    <div className="mr-pagination">
      <div className="mr-pagination__info">
        Showing {count} of {total} files
      </div>
      <div className="mr-pagination__controls">
        <button
          className="mr-pagination__page"
          disabled={page === 1}
          onClick={() => onPage(1)}
        >
          &laquo;
        </button>
        <button
          className="mr-pagination__page"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
        >
          {Ico.ChevR}
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
          return (
            <button
              key={p}
              className={`mr-pagination__page${page === p ? " active" : ""}`}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          );
        })}
        <button
          className="mr-pagination__page"
          disabled={page === totalPages}
          onClick={() => onPage(page + 1)}
        >
          {Ico.ChevR}
        </button>
        <button
          className="mr-pagination__page"
          disabled={page === totalPages}
          onClick={() => onPage(totalPages)}
        >
          &raquo;
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const MediaRepository = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  const [knownRooms, setKnownRooms] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [roomFilter, setRoomFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewMode, setViewMode] = useState("table");
  const [sortKey, setSortKey] = useState("uploadedAt");
  const [sortDir, setSortDir] = useState("desc");

  const [previewFile, setPreviewFile] = useState(null);
  const [quarantineTarget, setQuarantineTarget] = useState(null);
  const [quarantineLoading, setQuarantineLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [toast, setToast] = useState({ msg: "", type: "success" });
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3500);
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchMedia = useCallback(
    async (pg = 1) => {
      setLoading(true);
      setApiError(null);
      try {
        const statusParam =
          statusFilter === "All" ? "" : statusFilter.toLowerCase();
        const typeParam =
          typeFilter === "All" ? "" : TYPE_TO_API[typeFilter] || "";
        const selectedRoom = knownRooms.find((r) => r.name === roomFilter);
        const roomParam = roomFilter === "All" ? "" : selectedRoom?.id || "";

        const res = await getMediaRepositoryApi(
          pg,
          PAGE_SIZE,
          search,
          statusParam,
          typeParam,
          roomParam,
        );
        const data = res.data;
        const normalised = (data.media || []).map(normaliseMedia);
        setFiles(normalised);

        if (data.pagination) setPagination(data.pagination);

        setKnownRooms((prev) => {
          const existing = [...prev];
          normalised.forEach((f) => {
            if (f.room && !existing.find((r) => r.id === f.roomId)) {
              existing.push({ id: f.roomId, name: f.room });
            }
          });
          return existing;
        });
      } catch (err) {
        setApiError(err?.response?.data?.message || "Failed to load media.");
      } finally {
        setLoading(false);
      }
    },
    [search, statusFilter, typeFilter, roomFilter, knownRooms],
  );

  useEffect(() => {
    fetchMedia(1);
  }, [statusFilter, typeFilter, roomFilter]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => fetchMedia(1), 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: pagination.total || files.length,
      available: files.filter((f) => f.status === "available").length,
      quarantined: files.filter((f) => f.status === "quarantined").length,
      storageUsed: (() => {
        const b = files.reduce((s, f) => s + (f.sizeBytes || 0), 0);
        return b >= 1024 * 1024
          ? (b / 1024 / 1024).toFixed(1) + " MB"
          : Math.round(b / 1024) + " KB";
      })(),
    }),
    [files, pagination.total],
  );

  // ── Client-side sort ───────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    return [...files].sort((a, b) => {
      const aV = (a[sortKey] || "").toString().toLowerCase();
      const bV = (b[sortKey] || "").toString().toLowerCase();
      return sortDir === "asc" ? aV.localeCompare(bV) : bV.localeCompare(aV);
    });
  }, [files, sortKey, sortDir]);

  const totalPages = pagination.totalPages || 1;

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleQuarantine = async () => {
    setQuarantineLoading(true);
    try {
      await quarantineMediaApi(quarantineTarget.id);
      showToast(`"${quarantineTarget.name}" quarantined.`);
      setQuarantineTarget(null);
      fetchMedia(pagination.page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to quarantine file.",
        "error",
      );
    } finally {
      setQuarantineLoading(false);
    }
  };

  const handleRestore = async (file) => {
    try {
      await restoreMediaApi(file.id);
      showToast(`"${file.name}" restored.`);
      fetchMedia(pagination.page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to restore file.",
        "error",
      );
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteMediaApi(deleteTarget.id);
      showToast(`"${deleteTarget.name}" deleted.`);
      setDeleteTarget(null);
      fetchMedia(pagination.page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to delete file.",
        "error",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("All");
    setRoomFilter("All");
    setStatusFilter("All");
  };

  const SortTh = ({ col, label }) => (
    <th
      className={sortKey === col ? "sorted" : ""}
      onClick={() => handleSort(col)}
    >
      {label}
      <span className={`sort-icon${sortKey === col ? " active" : ""}`}>
        {Ico.SortUp}
      </span>
    </th>
  );

  const commonRowMenuProps = (file) => ({
    file,
    onView: setPreviewFile,
    onDownload: (f) => {
      if (f.downloadUrl && !f.downloadUrl.startsWith("mxc://")) {
        window.open(f.downloadUrl, "_blank");
      }
    },
    onQuarantine: setQuarantineTarget,
    onRestore: handleRestore,
    onDelete: setDeleteTarget,
  });

  return (
    <div className="mr-page">
      <Toast msg={toast.msg} type={toast.type} />

      {/* ── Header ── */}
      <div className="mr-header">
        <div className="mr-header__left">
          <span className="mr-header__label">Admin Portal</span>
          <h1 className="mr-header__title">Media Repository</h1>
          <p className="mr-header__subtitle">
            Browse, manage and control files shared across all rooms
          </p>
        </div>
        <div className="mr-header__right">
          <button
            className="mr-btn mr-btn--secondary"
            onClick={() => fetchMedia(pagination.page)}
            title="Refresh"
          >
            {Ico.Refresh}
          </button>
          <button className="mr-btn mr-btn--secondary">
            {Ico.Export} Export
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="mr-kpi-grid">
        {[
          {
            label: "Total Files",
            value: stats.total,
            color: "#0062FE",
            bg: "rgba(0,98,254,0.10)",
            icon: Ico.File,
          },
          {
            label: "Available",
            value: stats.available,
            color: "#22C55E",
            bg: "rgba(34,197,94,0.10)",
            icon: Ico.Eye,
          },
          {
            label: "Quarantined",
            value: stats.quarantined,
            color: "#F59E0B",
            bg: "rgba(245,158,11,0.10)",
            icon: Ico.Shield,
          },
          {
            label: "Storage Used",
            value: stats.storageUsed,
            color: "#8B5CF6",
            bg: "rgba(139,92,246,0.10)",
            icon: Ico.Database,
          },
        ].map((k) => (
          <div key={k.label} className="mr-kpi">
            <div
              className="mr-kpi__icon"
              style={{ background: k.bg, color: k.color }}
            >
              {k.icon}
            </div>
            <div className="mr-kpi__body">
              <div className="mr-kpi__value">{k.value}</div>
              <div className="mr-kpi__label">{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Storage Bar ── */}
      <StorageBar files={files} />

      {/* ── Toolbar ── */}
      <div className="mr-toolbar">
        <div className="mr-search-wrap">
          {Ico.Search}
          <input
            className="mr-search"
            placeholder="Search by name, uploader, room…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="mr-toolbar__filters">
          <select
            className="mr-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="All">All Types</option>
            {ALL_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <select
            className="mr-filter-select"
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
          >
            <option value="All">All Rooms</option>
            {knownRooms.map((r) => (
              <option key={r.id} value={r.name}>
                {r.name}
              </option>
            ))}
          </select>
          <select
            className="mr-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {["All", "Available", "Quarantined", "Archived"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="mr-toolbar-right">
          <div className="mr-view-toggle">
            <button
              className={viewMode === "table" ? "active" : ""}
              onClick={() => setViewMode("table")}
              title="Table view"
            >
              {Ico.List}
            </button>
            <button
              className={viewMode === "grid" ? "active" : ""}
              onClick={() => setViewMode("grid")}
              title="Grid view"
            >
              {Ico.Grid}
            </button>
          </div>
        </div>
      </div>

      {/* ── Error banner ── */}
      {apiError && (
        <div
          style={{
            padding: "12px 20px",
            background: "#FEE2E2",
            color: "#991B1B",
            fontSize: 13,
            borderRadius: 10,
            marginBottom: 16,
          }}
        >
          ⚠️ {apiError} &nbsp;
          <button
            style={{
              textDecoration: "underline",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#991B1B",
            }}
            onClick={() => fetchMedia(pagination.page)}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: 64,
            textAlign: "center",
            color: "#9CA3AF",
          }}
        >
          Loading media…
        </div>
      ) : viewMode === "grid" ? (
        <div className="mr-card" style={{ overflow: "visible" }}>
          {sorted.length === 0 ? (
            <div className="mr-empty">
              {Ico.File}
              <h4>No files found</h4>
              <p>Try adjusting your filters or search.</p>
              <button
                className="mr-btn mr-btn--secondary"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="mr-grid">
                {sorted.map((file) => (
                  <FileCard key={file.id} {...commonRowMenuProps(file)} />
                ))}
              </div>
              <Pagination
                page={pagination.page}
                totalPages={totalPages}
                total={pagination.total}
                count={files.length}
                onPage={fetchMedia}
              />
            </>
          )}
        </div>
      ) : (
        <div className="mr-card">
          <div className="mr-table-wrap">
            {sorted.length === 0 ? (
              <div className="mr-empty">
                {Ico.File}
                <h4>No files found</h4>
                <p>Try adjusting your search or filter.</p>
                <button
                  className="mr-btn mr-btn--secondary"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <table className="mr-table">
                <thead>
                  <tr>
                    <SortTh col="name" label="File" />
                    <SortTh col="type" label="Type" />
                    <SortTh col="room" label="Room" />
                    <SortTh col="uploadedBy" label="Uploaded By" />
                    <SortTh col="size" label="Size" />
                    <SortTh col="status" label="Status" />
                    <SortTh col="expiresAt" label="Expires" />
                    <th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((file) => {
                    const meta = getFileTypeMeta(file.type);
                    const hasMediaPreview =
                      (file.type?.toLowerCase() === "image" ||
                        file.type?.toLowerCase() === "video") &&
                      file.previewUrl &&
                      !file.previewUrl.startsWith("mxc://");
                    const isVideoRow = file.type?.toLowerCase() === "video";

                    return (
                      <tr
                        key={file.id}
                        className={
                          file.status === "quarantined"
                            ? "mr-row--quarantined"
                            : file.status === "archived"
                              ? "mr-row--archived"
                              : ""
                        }
                      >
                        {/* ── File cell ── */}
                        <td>
                          <div className="mr-file-cell">
                            {hasMediaPreview ? (
                              <div
                                className="mr-file-cell__icon"
                                style={{ padding: 0, overflow: "hidden" }}
                              >
                                {isVideoRow ? (
                                  <video
                                    src={file.previewUrl}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 6,
                                      display: "block",
                                      background: "#000",
                                    }}
                                    muted
                                    preload="metadata"
                                    onError={(e) => {
                                      if (e.target?.parentNode)
                                        e.target.parentNode.innerHTML = `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:${meta.bg};color:${meta.color};border-radius:6px;font-size:10px;font-weight:700">${meta.ext}</span>`;
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={file.previewUrl}
                                    alt={file.name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 6,
                                      display: "block",
                                    }}
                                    onError={(e) => {
                                      if (e.target?.parentNode)
                                        e.target.parentNode.innerHTML = `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:${meta.bg};color:${meta.color};border-radius:6px;font-size:10px;font-weight:700">${meta.ext}</span>`;
                                    }}
                                  />
                                )}
                              </div>
                            ) : (
                              <div
                                className="mr-file-cell__icon"
                                style={{
                                  background: meta.bg,
                                  color: meta.color,
                                }}
                              >
                                <span className="mr-file-cell__ext">
                                  {meta.ext}
                                </span>
                              </div>
                            )}
                            <div>
                              <div className="mr-file-cell__name">
                                {file.name}
                              </div>
                              {file.scanResult === "flagged" && (
                                <div className="mr-file-cell__flag">
                                  ⚠ Flagged by AV scan
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className="mr-type-chip"
                            style={{ color: meta.color, background: meta.bg }}
                          >
                            {file.type}
                          </span>
                        </td>
                        <td
                          style={{
                            fontSize: 13,
                            color: "#374151",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={file.room}
                        >
                          {file.room}
                        </td>
                        <td>
                          <div className="mr-manager-cell">
                            <div
                              className="mr-manager-cell__avatar"
                              style={{ background: file.uploaderColor }}
                            >
                              {file.uploaderInitials}
                            </div>
                            <div className="mr-manager-cell__name">
                              {file.uploadedBy}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            fontSize: 13,
                            color: "#6B7280",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.size}
                        </td>
                        <td>
                          <span className={`mr-badge mr-badge--${file.status}`}>
                            {file.status.charAt(0).toUpperCase() +
                              file.status.slice(1)}
                          </span>
                        </td>
                        <td
                          style={{
                            fontSize: 12.5,
                            color:
                              file.expiresAt === "Expired"
                                ? "#EF4444"
                                : "#6B7280",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.expiresAt}
                        </td>
                        <td>
                          <RowMenu {...commonRowMenuProps(file)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <Pagination
            page={pagination.page}
            totalPages={totalPages}
            total={pagination.total}
            count={files.length}
            onPage={fetchMedia}
          />
        </div>
      )}

      {/* ── Modals ── */}
      {previewFile && (
        <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
      {quarantineTarget && (
        <QuarantineModal
          file={quarantineTarget}
          loading={quarantineLoading}
          onConfirm={handleQuarantine}
          onCancel={() => setQuarantineTarget(null)}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          file={deleteTarget}
          loading={deleteLoading}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default MediaRepository;
